import {
  type DragEvent,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { DotsSixVerticalIcon } from "@phosphor-icons/react";
import {
  Box,
  Button,
  Callout,
  DropdownMenu,
  Flex,
  Link,
  ScrollArea,
  Select,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import {
  WidgetOverflowMenuItems,
  WidgetOverflowMenuItemsContext,
} from "~/v3-poc/components/Widget";
import { getPointerDropDirection } from "~/v3-poc/utils/dropDirection";
import { formatLastUpdatedAt } from "~/v3-poc/utils/lastUpdated";
import type { LegQuoteSide } from "../../shared/optionLegs/optionLeg.types";
import type { QuoteCellSelection } from "./OptionsChainGrid.pres";
import { OptionsChainGrid } from "./OptionsChainGrid.pres";
import type {
  ChainMetricId,
  MetricDropDirection,
  VisibleMetrics,
} from "./optionsChain.columns";
import {
  CHAIN_METRICS,
  completeMetricOrder,
  GREEK_METRIC_IDS,
  isChainMetricId,
} from "./optionsChain.columns";
import type {
  ChainEmptyReason,
  ChainFeedState,
  ChainLeg,
  ChainPermissions,
  ChainRow,
  ChainSection,
  ChainSideFilter,
  ExpirationCount,
  ExpirationFilter,
  ExpirationOption,
  ExpirationRange,
  StrikeCount,
  ValueFreshness,
} from "./optionsChain.types";
import {
  EXPIRATION_COUNT_OPTIONS,
  STRIKE_COUNT_OPTIONS,
} from "./optionsChain.types";

/** Below this the grid drops to bid, ask and strike only. */
const NARROW_WIDTH_PX = 520;

export interface OptionsChainWidgetPresentationProps {
  symbol?: string;
  /**
   * Asset search for this widget. Always passed — grouped chains show the
   * group's symbol in the same field.
   */
  symbolSearch?: ReactNode;
  /** Persistence failure after a grouped search pick or clear. */
  groupSymbolError?: string;
  expirations: readonly ExpirationOption[];
  expiration?: string;
  onExpirationChange: (expiration: string) => void;

  /** Ladders to render, one per stacked expiration. */
  sections: readonly ChainSection[];
  /** Every row across every section, for the empty and narrow-width checks. */
  rows: readonly ChainRow[];
  metrics: VisibleMetrics;
  freshnessByMetric: Readonly<Record<string, ValueFreshness>>;

  strikeCount: StrikeCount;
  onStrikeCountChange: (count: StrikeCount) => void;
  sideFilter: ChainSideFilter;
  onSideFilterChange: (value: ChainSideFilter) => void;
  expirationFilter: ExpirationFilter;
  onExpirationFilterChange: (value: ExpirationFilter) => void;
  /** The symbol has expirations before filtering, so the filter stays reachable. */
  hasExpirations: boolean;
  expirationRange: ExpirationRange;
  onExpirationRangeChange: (value: ExpirationRange) => void;
  expirationCount: ExpirationCount;
  onExpirationCountChange: (value: ExpirationCount) => void;
  visibleMetricIds: readonly ChainMetricId[];
  /** Scrollable column order in the picker and on the grid. */
  metricOrder: readonly ChainMetricId[];
  onMetricVisibilityChange: (
    ids: readonly ChainMetricId[],
    isVisible: boolean,
  ) => void;
  onReorderColumns: (
    source: ChainMetricId,
    target: ChainMetricId,
    direction: MetricDropDirection,
  ) => void;
  onSelectAllColumns: () => void;
  onResetMetrics: () => void;
  /** Set by the connector when the container is too narrow for the full set. */
  onNarrowChange: (isNarrow: boolean) => void;

  feedState: ChainFeedState;
  permissions: ChainPermissions;
  /** The widget is linked to a group, which is where a routed leg ends up. */
  hasGroup: boolean;
  /** Client receipt time of the newest snapshot payload. */
  lastUpdatedAt?: number;
  /**
   * The target order ticket already holds the maximum number of legs for the
   * symbol on show.
   */
  isTicketFull: boolean;

  isLoading: boolean;
  hasError: boolean;
  isRefreshError: boolean;
  onRetry: () => void;
  emptyReason?: ChainEmptyReason;
  /** True when the selected expiry has no Greeks at all, which 0DTE never does. */
  isGreeksUnavailable: boolean;
  isSpotUnavailable: boolean;
  /** Underlying price, marked in the ladder and pinned on screen. */
  spot?: number;

  selected: readonly QuoteCellSelection[];
  onQuoteSelect: (
    leg: ChainLeg,
    price: number | undefined,
    quoteSide: LegQuoteSide,
  ) => void;
  applyHref: string;
  /** Where a user goes to obtain real-time data. */
  realTimeHref: string;
}

/** A grouped chain with no symbol still has search, so it points there. */
const NO_SYMBOL_IN_GROUP_COPY = "No symbol — search for one.";

const EMPTY_COPY: Record<ChainEmptyReason, string> = {
  noSymbol: "No symbol — search for one, or pick a group.",
  noContracts: "No contracts for this expiration.",
  unsupportedSymbol: "This symbol has no listed options.",
  noExpirations: "No tradable expirations for this symbol.",
  noExpirationsInFilter: "No expirations match this filter.",
};

/**
 * Column picker. Bid and ask stay pinned; the rest nest under a Columns
 * submenu, published into the widget's 3-dot menu when that slot exists.
 * Rows are checkboxes with a drag handle so the list can be reordered.
 */
const COLUMN_REORDER_DRAG_TYPE = "application/x-options-chain-column";

const ChainColumnsMenu = ({
  visibleMetricIds,
  metricOrder,
  onMetricVisibilityChange,
  onReorderColumns,
  onSelectAllColumns,
  onResetMetrics,
}: Pick<
  OptionsChainWidgetPresentationProps,
  | "visibleMetricIds"
  | "metricOrder"
  | "onMetricVisibilityChange"
  | "onReorderColumns"
  | "onSelectAllColumns"
  | "onResetMetrics"
>) => {
  const overflowSlot = useContext(WidgetOverflowMenuItemsContext);
  const checkboxListId = useId();
  const [draggingMetricId, setDraggingMetricId] =
    useState<ChainMetricId | null>(null);
  const [columnsSubOpen, setColumnsSubOpen] = useState(false);
  const [dropIndicator, setDropIndicator] = useState<{
    metricId: ChainMetricId;
    direction: MetricDropDirection;
  } | null>(null);

  const orderedIds = completeMetricOrder(metricOrder);
  const visible = new Set(visibleMetricIds);
  const visibleCount = orderedIds.filter((id) => visible.has(id)).length;
  const greekCount = GREEK_METRIC_IDS.filter((id) => visible.has(id)).length;
  const allGreeks = greekCount === GREEK_METRIC_IDS.length;

  const clearColumnDrag = () => {
    setDraggingMetricId(null);
    setDropIndicator(null);
  };

  const handleDragStart = (
    event: DragEvent<HTMLElement>,
    metricId: ChainMetricId,
  ) => {
    event.dataTransfer.setData(COLUMN_REORDER_DRAG_TYPE, metricId);
    event.dataTransfer.setData("text/plain", metricId);
    event.dataTransfer.effectAllowed = "move";
    const row = event.currentTarget.closest("[data-chain-column-row]");
    if (row instanceof HTMLElement) {
      const rect = row.getBoundingClientRect();
      event.dataTransfer.setDragImage(
        row,
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
    }
    setDraggingMetricId(metricId);
    setDropIndicator(null);
  };

  const handleDragOver = (
    event: DragEvent<HTMLDivElement>,
    targetMetricId: ChainMetricId,
  ) => {
    if (!draggingMetricId) {
      return;
    }
    if (draggingMetricId === targetMetricId) {
      setDropIndicator(null);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const direction = getPointerDropDirection(
      "vertical",
      event.clientX,
      event.clientY,
      rect,
    );
    setDropIndicator((current) =>
      current?.metricId === targetMetricId && current.direction === direction
        ? current
        : { metricId: targetMetricId, direction },
    );
  };

  const handleDrop = (
    event: DragEvent<HTMLDivElement>,
    targetMetricId: ChainMetricId,
  ) => {
    event.preventDefault();
    const sourceMetricId =
      event.dataTransfer.getData(COLUMN_REORDER_DRAG_TYPE) ||
      event.dataTransfer.getData("text/plain");
    const direction =
      dropIndicator?.metricId === targetMetricId
        ? dropIndicator.direction
        : getPointerDropDirection(
            "vertical",
            event.clientX,
            event.clientY,
            event.currentTarget.getBoundingClientRect(),
          );
    clearColumnDrag();
    if (!isChainMetricId(sourceMetricId) || sourceMetricId === targetMetricId) {
      return;
    }
    onReorderColumns(sourceMetricId, targetMetricId, direction);
  };

  const items = (
    <>
      <DropdownMenu.Item
        disabled={visibleCount === orderedIds.length}
        onSelect={(event) => {
          event.preventDefault();
          onSelectAllColumns();
        }}
      >
        Select all
      </DropdownMenu.Item>
      <DropdownMenu.Item
        onSelect={(event) => {
          event.preventDefault();
          onResetMetrics();
        }}
      >
        Reset defaults
      </DropdownMenu.Item>
      <label className="flex cursor-pointer items-center gap-2 px-3 py-1">
        <input
          type="checkbox"
          checked={allGreeks}
          onChange={(event) =>
            onMetricVisibilityChange(GREEK_METRIC_IDS, event.target.checked)
          }
        />
        All Greeks
      </label>
      <DropdownMenu.Separator />
      <div
        className="flex flex-col"
        onPointerLeave={() => setDropIndicator(null)}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {orderedIds.map((id) => {
          const checkboxId = `${checkboxListId}-${id}`;
          const isVisible = visible.has(id);
          const isDragging = draggingMetricId === id;
          const dropDirection =
            dropIndicator?.metricId === id
              ? dropIndicator.direction
              : undefined;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: HTML5 drop target for column reorder
            <div
              key={id}
              data-chain-column-row={id}
              data-drop-indicator={dropDirection}
              className={cn(
                "flex items-center gap-2 px-2 py-1",
                isDragging &&
                  "rounded-[var(--radius-2)] bg-[var(--accent-a3)] opacity-60",
                dropDirection === "before" &&
                  "shadow-[inset_0_2px_0_0_var(--accent-9)]",
                dropDirection === "after" &&
                  "shadow-[inset_0_-2px_0_0_var(--accent-9)]",
              )}
              onDragOver={(event) => handleDragOver(event, id)}
              onDrop={(event) => handleDrop(event, id)}
            >
              <Flex
                as="span"
                align="center"
                justify="center"
                flexShrink="0"
                draggable
                aria-label={`Drag ${CHAIN_METRICS[id].label} to reorder`}
                className="cursor-grab"
                onPointerDown={(event) => event.stopPropagation()}
                onDragStart={(event) => handleDragStart(event, id)}
                onDragEnd={clearColumnDrag}
              >
                <DotsSixVerticalIcon
                  size={14}
                  aria-hidden
                  className="pointer-events-none"
                />
              </Flex>
              <label
                htmlFor={checkboxId}
                className="min-w-0 flex-1 cursor-pointer"
              >
                <span className="inline-flex items-center gap-2">
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={isVisible}
                    onChange={(event) =>
                      onMetricVisibilityChange([id], event.target.checked)
                    }
                  />
                  {CHAIN_METRICS[id].label}
                </span>
              </label>
            </div>
          );
        })}
      </div>
      <Text size="1" color="gray" className="px-3 py-1">
        Drag to reorder · uncheck to hide
      </Text>
    </>
  );

  const columnsSubmenu = (
    <DropdownMenu.Sub
      open={columnsSubOpen || draggingMetricId != null}
      onOpenChange={(open) => {
        if (!open && draggingMetricId != null) {
          return;
        }
        setColumnsSubOpen(open);
        if (!open) {
          clearColumnDrag();
        }
      }}
    >
      <DropdownMenu.SubTrigger>Columns</DropdownMenu.SubTrigger>
      <DropdownMenu.SubContent
        onPointerDownOutside={(event) => {
          if (draggingMetricId != null) {
            event.preventDefault();
            return;
          }
          clearColumnDrag();
        }}
        onFocusOutside={(event) => {
          if (draggingMetricId != null) {
            event.preventDefault();
          }
        }}
      >
        {items}
      </DropdownMenu.SubContent>
    </DropdownMenu.Sub>
  );

  if (overflowSlot) {
    return <WidgetOverflowMenuItems>{columnsSubmenu}</WidgetOverflowMenuItems>;
  }

  return (
    <DropdownMenu.Root
      onOpenChange={(open) => {
        if (!open) {
          clearColumnDrag();
        }
      }}
    >
      <DropdownMenu.Trigger>
        <Button
          size="1"
          variant="ghost"
          color="gray"
          aria-label="Configure columns"
        >
          Columns
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content size="1" align="end" variant="soft">
        {items}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

/** `12-Aug-26`, with `(W)` marking a weekly, as the reference platform does. */
const formatExpirationDate = (option: ExpirationOption) => {
  const [year, , day] = option.date.split("-");
  const monthName = new Date(`${option.date}T00:00:00Z`).toLocaleString(
    "en-US",
    { month: "short", timeZone: "UTC" },
  );
  const label = `${day}-${monthName}-${year.slice(2)}`;
  return option.cadence === "weekly" ? `${label} (W)` : label;
};

/**
 * Closed count menu. The trigger names the control and the current value
 * (`Strikes 10`); the list is just the numbers.
 */
const CountSelect = <T extends number>({
  label,
  ariaLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  ariaLabel: string;
  value: T;
  options: readonly T[];
  onChange: (value: T) => void;
}) => {
  const resolved = options.find((option) => option === value) ?? options[0];

  return (
    <Select.Root
      size="1"
      value={String(resolved)}
      onValueChange={(next) => {
        const parsed = Number(next);
        const match = options.find((option) => option === parsed);
        if (match !== undefined) {
          onChange(match);
        }
      }}
    >
      <Select.Trigger aria-label={ariaLabel}>
        {`${label} ${resolved}`}
      </Select.Trigger>
      <Select.Content>
        {options.map((option) => (
          <Select.Item key={option} value={String(option)}>
            {option}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
};

/**
 * Expiration picker: a single date-ordered list, with a date range at the
 * bottom for narrowing it. A menu rather than a tab bar because a liquid name
 * carries two dozen expirations, which no toolbar row can hold.
 *
 * The dates are not bucketed by cadence. Traders scan this list by date to find
 * the near month or the leg of a calendar, and any grouping breaks that scan by
 * splitting one timeline across several branches. `(W)` still marks the
 * weeklies, which is the part of the cadence worth knowing.
 */
const ExpirationMenu = ({
  expirations,
  expiration,
  onExpirationChange,
  expirationFilter,
  onExpirationFilterChange,
  expirationRange,
  onExpirationRangeChange,
}: Pick<
  OptionsChainWidgetPresentationProps,
  | "expirations"
  | "expiration"
  | "onExpirationChange"
  | "expirationFilter"
  | "onExpirationFilterChange"
  | "expirationRange"
  | "onExpirationRangeChange"
>) => {
  const selected = expirations.find((option) => option.date === expiration);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button size="1" variant="soft" color="gray" aria-label="Expiration">
          {selected ? formatExpirationDate(selected) : "Expiration"}
          <DropdownMenu.TriggerIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content size="1">
        {/*
          An action, not a mode. The chain shows one expiration at a time, so the
          only thing "all" can mean here is "stop narrowing the list", and a tick
          would compete with the date actually selected below.
        */}
        <DropdownMenu.Item
          disabled={expirationFilter === "all"}
          onSelect={() => {
            onExpirationFilterChange("all");
            onExpirationRangeChange({ from: "", to: "" });
          }}
        >
          All expirations
        </DropdownMenu.Item>

        <ScrollArea type="auto" scrollbars="vertical" className="max-h-[18rem]">
          {expirations.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.date}
              checked={option.date === expiration}
              onSelect={() => onExpirationChange(option.date)}
            >
              {formatExpirationDate(option)}
            </DropdownMenu.CheckboxItem>
          ))}
        </ScrollArea>

        <DropdownMenu.Separator />

        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger>Date range</DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            {/* Typing a date must not dismiss the menu, which a select would. */}
            <Flex
              direction="column"
              gap="1"
              p="1"
              onSelect={(event) => event.preventDefault()}
            >
              <TextField.Root
                size="1"
                type="date"
                aria-label="Expirations from"
                value={expirationRange.from}
                onChange={(event) => {
                  onExpirationFilterChange("range");
                  onExpirationRangeChange({
                    ...expirationRange,
                    from: event.currentTarget.value,
                  });
                }}
              />
              <TextField.Root
                size="1"
                type="date"
                aria-label="Expirations to"
                value={expirationRange.to}
                onChange={(event) => {
                  onExpirationFilterChange("range");
                  onExpirationRangeChange({
                    ...expirationRange,
                    to: event.currentTarget.value,
                  });
                }}
              />
            </Flex>
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

/** Poll timestamp (and a failed refresh, when that is why it looks stale). */
const ChainFilterStatus = ({
  lastUpdatedLabel,
  isRefreshError,
  onRetryClick,
}: {
  lastUpdatedLabel?: string;
  isRefreshError: boolean;
  onRetryClick: (event: React.MouseEvent) => void;
}) => {
  if (!lastUpdatedLabel && !isRefreshError) {
    return null;
  }

  return (
    <Flex
      align="center"
      gap="2"
      wrap="nowrap"
      flexShrink="0"
      className="ml-auto"
    >
      {isRefreshError ? (
        <Text size="1" color="orange">
          Couldn&apos;t refresh.{" "}
          <Link size="1" href="#" onClick={onRetryClick}>
            Retry
          </Link>
        </Text>
      ) : null}
      {lastUpdatedLabel ? (
        <Text size="1" color="gray" wrap="nowrap" className="atd-numeric">
          {lastUpdatedLabel}
        </Text>
      ) : null}
    </Flex>
  );
};

export const OptionsChainWidgetPresentation = ({
  symbol,
  symbolSearch,
  groupSymbolError,
  expirations,
  expiration,
  onExpirationChange,
  sections,
  rows,
  metrics,
  freshnessByMetric,
  strikeCount,
  onStrikeCountChange,
  sideFilter,
  onSideFilterChange,
  expirationFilter,
  onExpirationFilterChange,
  hasExpirations,
  expirationRange,
  onExpirationRangeChange,
  expirationCount,
  onExpirationCountChange,
  visibleMetricIds,
  metricOrder,
  onMetricVisibilityChange,
  onReorderColumns,
  onSelectAllColumns,
  onResetMetrics,
  onNarrowChange,
  feedState,
  permissions,
  hasGroup,
  lastUpdatedAt,
  isTicketFull,
  isLoading,
  hasError,
  isRefreshError,
  onRetry,
  emptyReason,
  isGreeksUnavailable,
  isSpotUnavailable,
  spot,
  selected,
  onQuoteSelect,
  applyHref,
  realTimeHref,
}: OptionsChainWidgetPresentationProps) => {
  // Every column is polled on a three-second interval, so the header advertises
  // freshness rather than a live connection.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  // Fit is measured from the widget's own box, not the viewport: two chains can
  // sit side by side at different widths in the same workspace.
  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      const narrow =
        element.clientWidth > 0 && element.clientWidth < NARROW_WIDTH_PX;
      setIsNarrow(narrow);
      onNarrowChange(narrow);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [onNarrowChange]);

  // Only a confirmed indicative feed marks the columns. Until `X-Feed` says so the
  // feed is unknown, and calling the prices delayed would be a guess: rows render
  // from the contracts request, which can land before the snapshots request that
  // carries the header, so "unknown" is a real state and not just a first frame.
  const isDelayedFeed = feedState.feed === "indicative";
  const noSymbolCopy = hasGroup ? NO_SYMBOL_IN_GROUP_COPY : EMPTY_COPY.noSymbol;
  const lastUpdatedLabel = formatLastUpdatedAt(lastUpdatedAt);
  const onRetryClick = (event: React.MouseEvent) => {
    event.preventDefault();
    onRetry();
  };

  return (
    <Flex
      ref={containerRef}
      direction="column"
      height="100%"
      minHeight="0"
      overflow="hidden"
      className="min-h-0"
    >
      <ChainColumnsMenu
        visibleMetricIds={visibleMetricIds}
        metricOrder={metricOrder}
        onMetricVisibilityChange={onMetricVisibilityChange}
        onReorderColumns={onReorderColumns}
        onSelectAllColumns={onSelectAllColumns}
        onResetMetrics={onResetMetrics}
      />
      <Flex
        align="center"
        justify="between"
        gap="2"
        px="2"
        py="1"
        wrap="wrap"
        flexShrink="0"
      >
        <Flex align="center" gap="2" wrap="wrap" className="min-w-0">
          {symbolSearch ? <Box width="120px">{symbolSearch}</Box> : null}

          {hasExpirations ? (
            <ExpirationMenu
              expirations={expirations}
              expiration={expiration}
              onExpirationChange={onExpirationChange}
              expirationFilter={expirationFilter}
              onExpirationFilterChange={onExpirationFilterChange}
              expirationRange={expirationRange}
              onExpirationRangeChange={onExpirationRangeChange}
            />
          ) : null}

          {hasExpirations ? (
            <CountSelect
              label="Expiries"
              ariaLabel="Expirations shown"
              value={expirationCount}
              options={EXPIRATION_COUNT_OPTIONS}
              onChange={onExpirationCountChange}
            />
          ) : null}

          <CountSelect
            label="Strikes"
            ariaLabel="Strikes around the money"
            value={strikeCount}
            options={STRIKE_COUNT_OPTIONS}
            onChange={onStrikeCountChange}
          />

          <Select.Root
            size="1"
            value={sideFilter}
            onValueChange={(value) =>
              onSideFilterChange(value as ChainSideFilter)
            }
          >
            <Select.Trigger aria-label="Calls or puts" />
            <Select.Content>
              <Select.Item value="both">Calls and puts</Select.Item>
              <Select.Item value="call">Calls only</Select.Item>
              <Select.Item value="put">Puts only</Select.Item>
            </Select.Content>
          </Select.Root>
        </Flex>

        <ChainFilterStatus
          lastUpdatedLabel={lastUpdatedLabel}
          isRefreshError={isRefreshError && rows.length > 0}
          onRetryClick={onRetryClick}
        />
      </Flex>

      {groupSymbolError ? (
        <Box px="2" pb="1" flexShrink="0">
          <Text role="alert" size="1" color="red">
            {groupSymbolError}
          </Text>
        </Box>
      ) : null}

      {!symbol ? (
        // Checked before rows: without a symbol there is nothing to show, even
        // if a previous underlying left rows behind.
        <Flex align="center" justify="center" p="4" flexGrow="1">
          <Text size="1" color="gray" align="center">
            {noSymbolCopy}
          </Text>
        </Flex>
      ) : hasError && rows.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="2"
          p="4"
          flexGrow="1"
        >
          <Callout.Root size="1" color="red" role="alert">
            <Callout.Text size="1">
              Couldn&apos;t load the options chain.
            </Callout.Text>
          </Callout.Root>
          <Button size="1" variant="outline" color="gray" onClick={onRetry}>
            Retry
          </Button>
        </Flex>
      ) : isLoading && rows.length === 0 ? (
        <Flex
          align="center"
          justify="center"
          gap="2"
          p="4"
          flexGrow="1"
          role="status"
        >
          <Spinner size="2" />
          <Text size="1" color="gray">
            Loading options chain…
          </Text>
        </Flex>
      ) : rows.length === 0 ? (
        <Flex align="center" justify="center" p="4" flexGrow="1">
          <Text size="1" color="gray" align="center">
            {EMPTY_COPY[emptyReason ?? "noContracts"]}
          </Text>
        </Flex>
      ) : (
        <OptionsChainGrid
          sections={sections}
          metrics={metrics}
          symbol={symbol ?? ""}
          freshnessByMetric={freshnessByMetric}
          canTrade={permissions.canTrade}
          hasGroup={hasGroup}
          selected={selected}
          isTicketFull={isTicketFull}
          isDelayedFeed={isDelayedFeed}
          sideFilter={sideFilter}
          realTimeHref={realTimeHref}
          applyHref={applyHref}
          isGreeksUnavailable={isGreeksUnavailable}
          isSpotUnavailable={isSpotUnavailable}
          spot={spot}
          onQuoteSelect={onQuoteSelect}
        />
      )}

      {isNarrow && rows.length > 0 ? (
        <Box px="2" py="1" flexShrink="0">
          <Text size="1" color="gray">
            Widen the widget for more columns.
          </Text>
        </Box>
      ) : null}
    </Flex>
  );
};
